import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import WebSocketService from '../services/websocket';
import { useAuth } from './AuthContext';

// Create WebSocket context
const WebSocketContext = createContext(null);

/**
 * WebSocket Provider Component
 * Provides WebSocket functionality to the entire application
 */
export const WebSocketProvider = ({ children, wsUrl }) => {
    const [webSocketService] = useState(() => WebSocketService.getInstance(wsUrl || process.env.REACT_APP_WEBSOCKET_URL));
    const [connected, setConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    const auth = useAuth();
    const token = auth?.token;

    // Handle incoming messages
    const handleMessage = useCallback((data) => {
        setLastMessage(data);
    }, []);

    // const handleNotificationMessages = useCallback((data) => {
    //     console.log("Received WS Message for notification => ", data);
    //     setLastMessage(data);
    // }, []);

    // Handle connection status changes
    const handleConnectionChange = useCallback((status) => {
        setConnected(status);
    }, []);

    // Connect to WebSocket service
    const connect = useCallback(async () => {
        try {
            if (!connected && token) {
                // Use the token for Authorization header in the WebSocket connection
                await webSocketService.connect(token);
                setConnected(true);
            } else if (!token) {
                return false;
            }
            return true;
        } catch (error) {
            setConnected(false);
            return false;
        }
    }, [webSocketService, connected, token]);

    // Disconnect from WebSocket service
    const disconnect = useCallback(() => {
        webSocketService.disconnect();
        setConnected(false);
    }, [webSocketService]);

    // Send message to WebSocket service
    const sendMessage = useCallback((message) => {
        if (!token) {
            console.warn('Cannot send message: No authentication token available');
            return false;
        }
        return webSocketService.send(message);
    }, [webSocketService, token]);

    // Subscribe to WebSocket events
    const subscribe = useCallback((event, callback) => {
        webSocketService.on(event, callback);
        // Return unsubscribe function
        return () => webSocketService.off(event, callback);
    }, [webSocketService]);

    // Setup event listeners when component mounts
    useEffect(() => {
        // Setup message listener
        webSocketService.on("message", handleMessage);
        // webSocketService.on("form_event", handleNotificationMessages);

        // Setup connection status listeners
        const handleOpen = () => handleConnectionChange(true);
        const handleClose = () => handleConnectionChange(false);
        const handleError = () => handleConnectionChange(false);

        webSocketService.on('open', handleOpen);
        webSocketService.on('close', handleClose);
        webSocketService.on('error', handleError);

        // Connect if token is available
        if (token) {
            connect();
        } else {
            console.warn('WebSocket connection not established: Missing authentication token');
            setConnected(false);
        }

        // Cleanup listeners when component unmounts
        return () => {
            // webSocketService.off("form_event", handleNotificationMessages);
            webSocketService.off('message', handleMessage);
            webSocketService.off('open', handleOpen);
            webSocketService.off('close', handleClose);
            webSocketService.off('error', handleError);
            // Don't automatically disconnect when unmounting
            // This prevents reconnection loops when components remount
            // Let the connection persist until explicitly disconnected
        };
    }, [webSocketService, handleMessage, handleConnectionChange, connect, disconnect, token]);

    // Context value
    const value = {
        connected,
        lastMessage,
        connect,
        disconnect,
        sendMessage,
        subscribe,
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};

/**
 * Custom hook to use WebSocket context
 * @returns {Object} WebSocket context value
 */
export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};

export default WebSocketContext;